
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/ui/use-toast';
import { ArrowDownToLine, RefreshCcw } from 'lucide-react';

const PwaUpdateNotifier = () => {
  const { t } = useTranslation();
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const lastAssetUpdateRef = useRef<number>(0);
  
  // Debounce function to limit frequency of asset update notifications
  const debounceAssetUpdate = (callback: () => void, delay: number) => {
    const now = Date.now();
    if (now - lastAssetUpdateRef.current > delay) {
      callback();
      lastAssetUpdateRef.current = now;
    } else {
      console.log('Asset update notification debounced');
    }
  };

  useEffect(() => {
    const onServiceWorkerUpdate = (registration: ServiceWorkerRegistration) => {
      setWaitingWorker(registration.waiting);
      setNewVersionAvailable(true);
      
      toast({
        title: t('common.updateAvailable', 'Update Available'),
        description: t('common.updateAvailableDesc', 'A new version of the app is available.'),
        action: (
          <button
            onClick={() => {
              if (waitingWorker) {
                // Send skip waiting message to the service worker
                waitingWorker.postMessage({ type: 'SKIP_WAITING' });
                // Reload the page to apply the update
                window.location.reload();
              }
            }}
            className="px-3 py-2 rounded bg-[#d32f2f] text-white hover:bg-[#b71c1c] flex items-center gap-1"
          >
            <RefreshCcw className="h-4 w-4" />
            {t('common.update', 'Update')}
          </button>
        ),
        duration: 0, // Don't automatically dismiss
      });
    };
    
    // Check if service workers are supported
    if ('serviceWorker' in navigator) {
      // Listen for new service worker installation
      navigator.serviceWorker.ready.then(registration => {
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                onServiceWorkerUpdate(registration);
              }
            });
          }
        });
      });
      
      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', event => {
        if (event.data && event.data.type === 'ASSETS_UPDATED') {
          console.log('Received updated assets from service worker:', event.data.data);
          
          // Debounce asset update notifications (only show once every 5 minutes)
          debounceAssetUpdate(() => {
            // Display notification about assets being updated
            toast({
              title: t('common.assetsUpdated', 'App Assets Updated'),
              description: t('common.assetsUpdatedDesc', 'App appearance has been updated with the latest settings.'),
              action: (
                <div className="flex items-center gap-1">
                  <ArrowDownToLine className="h-4 w-4" />
                </div>
              ),
              duration: 5000, // Show for 5 seconds instead of 3
            });
          }, 5 * 60 * 1000); // 5 minutes debounce
        }
      });
      
      // Check for updates on page load
      const checkForUpdates = async () => {
        try {
          const registration = await navigator.serviceWorker.getRegistration();
          if (registration && registration.waiting) {
            onServiceWorkerUpdate(registration);
          }
        } catch (error) {
          console.error('Error checking for service worker updates:', error);
        }
      };
      
      checkForUpdates();
      
      // Check for updates periodically (every 60 minutes)
      const intervalId = setInterval(() => {
        navigator.serviceWorker.getRegistration().then(registration => {
          if (registration) {
            registration.update().catch(err => {
              console.error('Error updating service worker:', err);
            });
          }
        });
      }, 60 * 60 * 1000);
      
      return () => {
        clearInterval(intervalId);
      };
    }
  }, [t, waitingWorker]);

  // This component doesn't render anything visible - it just manages the update notification
  return null;
};

export default PwaUpdateNotifier;
